import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {useHistory, useParams} from 'src/rerouter';

import type {ApplicationReviewCardProps} from 'src/types/crm';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardActions,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  ButtonTextMedium,
  SubTitleExtraSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import SubmitFeedbacks from 'src/components/crm/jobs/job-info/overview/feedbacks/submit-feedbacks';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';

import css from './application-review-card.css';


const ApplicationReviewCard = ({
  applicantReviewData,
  from = 'DASHBOARD',
}: ApplicationReviewCardProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const history = useHistory();
  const {
    stepCandidateId,
    candidateId,
    jobId: queryJobId,
    feedbackTabId,
    type,
    isReviewPanelOpen,
  } = history.location.query;
  const {jobId: paramJobId} = useParams();
  const jobId = from === 'DASHBOARD' ? queryJobId : paramJobId;

  const handleSubmitReview = () => {
    const params = new URLSearchParams(window.location.search);
    params.set('stepCandidateId', applicantReviewData.stepCandidateId);
    params.set('candidateId', applicantReviewData.candidateId.toString());
    if (from === 'DASHBOARD') {
      params.set('jobId', applicantReviewData.jobId);
    }
    params.set('feedbackTabId', 'RESUME');
    params.set('type', 'applicant');
    params.set('isReviewPanelOpen', 'true');
    const rerouteUrl =
      from === 'DASHBOARD'
        ? `/dashboard/feedbacks?${params.toString()}`
        : `/crm/job/${jobId}/info/overview?${params.toString()}`;
    history.push(rerouteUrl);
  };

  const reviewItems = [
    {
      label: `${t('Candidate', 'Candidate')}: ${
        applicantReviewData.candidateName
      }`,
      icon: 'user',
    },
    {
      label: `${t('Job', 'Job')}: ${applicantReviewData.jobTitle}`,
      icon: 'briefcase',
    },
    {
      label: applicantReviewData.stepName,
      icon: 'compass',
    },
  ];

  return (
    <>
      {isReviewPanelOpen === 'true' && (
        <SubmitFeedbacks
          isOpen={isReviewPanelOpen === 'true'}
          handleClose={() => {
            if (from === 'DASHBOARD') {
              history.push(`/dashboard`);
            } else if (from === 'JOB_OVERVIEW' && jobId) {
              history.push(`/crm/job/${jobId}/info/overview`);
            }
          }}
          candidateStepId={stepCandidateId}
          candidateId={Number(candidateId)}
          jobId={jobId}
          tabId={feedbackTabId}
          type={type}
          from={from}
        />
      )}
      <Card
        classNames={{
          wrapper: css.innerCard,
        }}
      >
        <div className={css.cardHeader}>
          <ButtonTextMedium>
            <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 6}}>
              {applicantReviewData.candidateName}
            </TruncatedTextWithTooltip>
          </ButtonTextMedium>
          <CardActions>
            <Button
              children={t('Submit_Review', 'Submit Review')}
              onClick={handleSubmitReview}
              type="tertiary"
              size="small"
              iconLeftName="file-lines"
            />
          </CardActions>
        </div>
        <CardContent className={css.cardContent}>
          <Card
            classNames={{
              wrapper: css.innerApplicationReviewCard,
            }}
            paddingTop="small"
            paddingBottom="small"
          >
            {reviewItems.map(({label, icon}) => (
              <div className={css.reviewItem}>
                <Icon name={icon} size="small" />
                <SubTitleExtraSmall>{label}</SubTitleExtraSmall>
              </div>
            ))}
          </Card>
        </CardContent>
      </Card>
    </>
  );
};

export default ApplicationReviewCard;
